import React, {
  useState,
  useEffect,
  useRef,
  FormEvent,
  useCallback,
  memo,
} from "react";
import {
  Icon,
  Button,
  Form,
  Modal,
  Input,
  Dropdown,
  Popup,
  Accordion,
  Table,
} from "semantic-ui-react";
import {
  uploadFile,
  FirmwareType,
  editFirmware,
  addOrUpdateFirmwareDependency,
} from "../../../../../BytebeamClient";
import { Mixpanel } from "../../../common/MixPanel";
import AnimatedEllipsis from "../../../common/AnimatedEllipsis";
import {
  StyledFileInput,
  StyledFileUploadButton,
} from "../../../DeviceManagement/Devices/ActionModals/SendFileModal";
import { beamtoast } from "../../../../common/CustomToast";
import { getHumanReadableFileSizeString } from "../../../util";
import { OptionType, validateFirmwareVersion } from "../../util";
import {
  ComponentWiseFirmwareRow,
  DependencyType,
} from "./UpdateFirmwareDependencies";

type CreateFirmwareModalProps = {
  readonly type: "Add" | "Edit";
  readonly selectedFirmwareVersion?: FirmwareType;
  readonly componentsEnabled?: boolean;
  readonly componentsList?: string[];
  readonly loadingComponentList?: boolean;
  readonly component: string;
  readonly firmwareVersions?: FirmwareType[];
  readonly isOpen: boolean;
  readonly close: () => void;
  readonly fillFirmwareTable?: () => Promise<void>;
  readonly allExistingFirmwareVersions: FirmwareType[];

  readonly setOptionType?: (arg: OptionType) => void;
  readonly setSelectedVersion?: (version: string) => void;
  readonly setUploadedFileName?: (name: string) => void;
};

const CreateOrEditFirmwareModal = memo((props: CreateFirmwareModalProps) => {
  const firmwareFileInput = useRef<HTMLInputElement | null>(null);

  const [firmwareVersion, setFirmwareVersion] = useState<string>("");
  const [existingFirmwareVersions, setExistingFirmwareVersions] = useState<
    Set<string>
  >(new Set());

  const [uncompressedSize, setUncompressedSize] = useState<number | undefined>(
    undefined
  );
  const [previousUncompressedSize, setPreviousUncompressedSize] = useState<
    number | undefined
  >(undefined);
  const [unit, setUnit] = useState<string>("KiB");
  const [previouslySelectedUnit, setPreviouslySelectedUnit] =
    useState<string>("KiB");

  const [firmwareFile, setFirmwareFile] = useState<File>(
    new File([""], "filename")
  );
  const [firmwareFileName, setFirmwareFileName] = useState<string>("");
  const [firmwareFileLoaded, setFirmwareFileLoaded] = useState<number>(0);
  const [firmwareFileTotal, setFirmwareFileTotal] = useState<number>(0);
  const [showFirmwareUploadProgress, setShowFirmwareUploadProgress] =
    useState<boolean>(false);
  const [showFirmwareEditProgress, setShowFirmwareEditProgress] =
    useState<boolean>(false);
  const [uploadedFirmwareFileResponse, setUploadedFirmwareFileResponse] =
    useState<{
      status: number;
      data: { id: string };
    }>({
      status: 0,
      data: { id: "" },
    });

  const [disableUploadButton, setDisableUploadButton] = useState<boolean>(true);
  const [errors, setErrors] = useState<{ versionNumber: string | null }>({
    versionNumber: "",
  });
  const [selectedComponent, setSelectedComponent] = useState<string>(
    props.component
  );

  const [addDependencies, setAddDependencies] = useState<boolean>(false);
  const [dependencies, setDependencies] = useState<DependencyType[]>([
    {
      id: 0,
      component_name: "",
      min_version: "",
    },
  ]);

  const addDependency = async () => {
    let dependencyList = dependencies.flatMap((dependency) => {
      if (dependency.component_name !== "") {
        return [
          {
            component_name: dependency.component_name,
            min_version: dependency.min_version,
          },
        ];
      } else {
        return [];
      }
    });

    try {
      await addOrUpdateFirmwareDependency(
        selectedComponent,
        firmwareVersion,
        dependencyList
      );
    } catch (error) {
      console.log(error);
      beamtoast.error(
        `Failed to add dependencies for firmware version ${firmwareVersion} of ${selectedComponent} component`
      );
    }
  };

  const createOrEditFirmware = async () => {
    const createUrl = "/api/v1/firmwares";

    // const compressedExtensions = ["zip", "tar", "rar", "7z", "gz", "bz2"];
    // const fileExtension = file?.name.split(".")?.pop()?.toLowerCase() ?? "";

    let formData = new FormData();
    formData.append("version-number", firmwareVersion); // append in both cases

    if (props.type === "Add") {
      // file checking only when creating new firmware
      if (firmwareFile !== null) {
        formData.append("file", firmwareFile);
      } else {
        beamtoast.error("A firmware file must be selected!");
        return;
      }

      if (props.componentsEnabled) {
        if (selectedComponent !== "") {
          formData.append("component-name", selectedComponent);
        } else {
          beamtoast.error("Please select a device component!");
          return;
        }
      }
      if (
        dependencies?.[0]?.component_name !== "" &&
        dependencies.some((dependency) => dependency?.min_version === "")
      ) {
        for (let i = 0; i < dependencies?.length; i++) {
          if (
            dependencies?.[i]?.component_name !== "" &&
            dependencies?.[i]?.min_version === ""
          ) {
            beamtoast.error(
              `Please select the minimum firmware version for "${dependencies?.[i]?.component_name}" component`
            );
            break;
          }
        }
        return;
      }
    }

    let uploadSize: string = "";

    if (uncompressedSize !== undefined) {
      if (uncompressedSize <= 0) {
        beamtoast.error("Uncompressed size must be greater than 0!");
        return;
      } else {
        let factor = 1;

        if (unit === "KiB") {
          factor = 1024;
        } else if (unit === "MiB") {
          factor = 1024 ** 2;
        } else if (unit === "GiB") {
          factor = 1024 ** 3;
        }

        // Calculate upload size
        uploadSize = String(Math.ceil(uncompressedSize * factor));

        // Append the calculated size
        formData.append("uncompressed-size", uploadSize);
      }
    }

    try {
      setDisableUploadButton(true);

      let res: any = null;

      if (props.type === "Edit") {
        setShowFirmwareEditProgress(true);
        await editFirmware(firmwareVersion, Number(uploadSize));

        beamtoast.success(
          `Updated firmware version ${props.selectedFirmwareVersion?.["version_number"]}`
        );
      } else {
        setShowFirmwareUploadProgress(true);

        res = await uploadFile(createUrl, formData, (p) => {
          // for loading progress
          setFirmwareFileLoaded(p.loaded);
          setFirmwareFileTotal(p.total);
        });

        setUploadedFirmwareFileResponse(res);

        if (res.status === 201) {
          if (
            props.setOptionType &&
            props.setSelectedVersion &&
            props.setUploadedFileName
          ) {
            props.setOptionType(OptionType.UploadFirmware);
            props.setSelectedVersion(res.data["version_number"]);
            props.setUploadedFileName(res.data["file_name"]);
          }

          beamtoast.success(
            "Created Firmware version " + res.data["version_number"]
          );

          Mixpanel.track("Uploaded Firmware", {
            Firmware: res.data["version_number"],
          });

          if (
            props.componentsEnabled &&
            props.type === "Add" &&
            dependencies?.[0]?.component_name !== ""
          ) {
            await addDependency();
          }
        }
      }

      if (props.fillFirmwareTable) await props.fillFirmwareTable();
    } catch (error) {
      if (props.type === "Edit") {
        console.log(error);
        beamtoast.error("Error updating firmware!");
      } else {
        Mixpanel.track("Failure", {
          type: "Upload Firmware",
        });
        // Handling Error for File Size exceeding limit from NGINX
        if (String(error).includes("413"))
          beamtoast.error("Upload failed due to size limit!");
        else beamtoast.error("Failed to create Firmware version :(");
      }
    } finally {
      onCloseModal();
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (props.type === "Add") {
      let versionNumber = firmwareVersion.trim();

      if (versionNumber?.length === 0) {
        setErrors({
          versionNumber: "Please enter firmware version number",
        });
      } else if (existingFirmwareVersions.has(versionNumber)) {
        setErrors({
          versionNumber: "This firmware version already exists",
        });
      } else if (
        !validateFirmwareVersion(versionNumber) &&
        props.componentsEnabled
      ) {
        setErrors({
          versionNumber:
            "Please enter a semantic version number, e.g. 1.0.0, 1.0.0-beta etc...",
        });
      } else if (
        firmwareFileInput?.current?.files &&
        firmwareFileInput.current.files?.length === 0
      ) {
        setTimeout(() => beamtoast.error("A firmware file must be selected!"));
      } else {
        await createOrEditFirmware();
      }
    } else if (props.type === "Edit") {
      if (
        (uncompressedSize === previousUncompressedSize &&
          unit === previouslySelectedUnit) ||
        (Number.isNaN(uncompressedSize) &&
          previousUncompressedSize === undefined)
      ) {
        beamtoast.error("No change in values detected!");
      } else {
        await createOrEditFirmware();
      }
    }
  };

  const onSelect = useCallback((e) => {
    setShowFirmwareUploadProgress(false);
    setFirmwareFileLoaded(0);
    setFirmwareFileTotal(0);

    if (e.target.files?.length !== 0) {
      setFirmwareFile(e.target.files[0]);
      setFirmwareFileName(e.target.files[0].name);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state and close modal
  function onCloseModal() {
    // Reset state
    setFirmwareVersion("");
    setExistingFirmwareVersions(new Set());
    setFirmwareFile(new File([""], "filename"));
    setFirmwareFileName("");
    setErrors({
      versionNumber: null,
    });
    setFirmwareFileLoaded(0);
    setFirmwareFileTotal(0);
    setShowFirmwareUploadProgress(false);
    setDisableUploadButton(true);
    setUnit("KiB");
    setPreviouslySelectedUnit("KiB");
    setUncompressedSize(undefined);
    setPreviousUncompressedSize(undefined);
    setSelectedComponent("");
    setUploadedFirmwareFileResponse({
      status: 0,
      data: { id: "" },
    });
    setShowFirmwareEditProgress(false);
    setAddDependencies(false);
    setDependencies([
      {
        id: 0,
        component_name: "",
        min_version: "",
      },
    ]);

    // Close Modal
    props.close();
  }

  useEffect(() => {
    if (props.type === "Edit" && props.selectedFirmwareVersion) {
      setFirmwareVersion(props.selectedFirmwareVersion?.version_number);
      setFirmwareFileName(props.selectedFirmwareVersion.file_name);
      if (props.selectedFirmwareVersion.device_component_name)
        setSelectedComponent(
          props.selectedFirmwareVersion.device_component_name
        );

      const size = props.selectedFirmwareVersion?.uncompressed_size;

      if (size && props.isOpen) {
        const sizeData: string[] =
          getHumanReadableFileSizeString(size).split(" ");

        setUncompressedSize(Number(sizeData[0]));
        setPreviousUncompressedSize(Number(sizeData[0]));

        setUnit(sizeData[1]);
        setPreviouslySelectedUnit(sizeData[1]);
      }
    } else {
      setSelectedComponent(props.component);
    }
  }, [props.isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      (uncompressedSize === previousUncompressedSize &&
        unit === previouslySelectedUnit) ||
      (Number.isNaN(uncompressedSize) && previousUncompressedSize === undefined)
    )
      setDisableUploadButton(true);
    else setDisableUploadButton(false);
  }, [
    uncompressedSize,
    unit,
    previousUncompressedSize,
    previouslySelectedUnit,
  ]);

  useEffect(() => {
    const existingVersions: Set<string> = new Set();

    props.allExistingFirmwareVersions.forEach((firmware: FirmwareType) => {
      if (props.componentsEnabled) {
        if (firmware.device_component_name === selectedComponent)
          existingVersions.add(firmware.version_number);
      } else {
        existingVersions.add(firmware.version_number);
      }
    });

    setExistingFirmwareVersions(existingVersions);
  }, [
    props.isOpen,
    props.allExistingFirmwareVersions,
    selectedComponent,
    props.componentsEnabled,
  ]);

  useEffect(() => {
    if (
      firmwareVersion &&
      firmwareVersion.trim()?.length !== 0 &&
      firmwareFile.name?.length !== 0
    ) {
      setDisableUploadButton(false);
    } else {
      setDisableUploadButton(true);
    }
  }, [firmwareVersion, firmwareFile, uncompressedSize]);

  return (
    <Modal
      open={props.isOpen}
      onClose={onCloseModal}
      size={props.componentsEnabled && props.type === "Add" ? "small" : "tiny"}
      className="dark"
      closeOnDimmerClick={false}
      closeOnEscape={false}
    >
      <Modal.Header>
        {props.type === "Edit" ? "Edit" : "Create New"} Firmware Version
      </Modal.Header>

      <Modal.Content>
        <Form>
          {props.componentsEnabled && (
            <Form.Field>
              <label htmlFor="firmware_version_number">
                Select Device Component
              </label>
              <Dropdown
                placeholder="Device Component"
                id="firmware_component_selection"
                selection
                options={props.componentsList?.map((component) => ({
                  key: component,
                  value: component,
                  text: component,
                }))}
                disabled={props.type === "Edit"}
                value={selectedComponent}
                onChange={(e, { value }) => {
                  setSelectedComponent(value as string);
                }}
                style={{ minWidth: "100px" }}
              />
            </Form.Field>
          )}
          <Form.Field>
            <label htmlFor="firmware_version_number">
              Firmware Version Number{" "}
              {props.componentsEnabled && (
                <Popup
                  inverted
                  trigger={
                    <Icon
                      name="question circle"
                      style={{ marginBottom: "2px", marginLeft: "2px" }}
                    />
                  }
                  content={
                    "Only unique semantic version number of the firmware are allowed For example, 1.0.0, 1.0.0-beta, etc."
                  }
                  position="top center"
                />
              )}
            </label>
            <Form.Input
              id="firmware_version_number"
              autoFocus
              autoComplete="off"
              error={errors.versionNumber !== "" ? errors.versionNumber : null}
              placeholder="1.0.0, 1.0.0-beta, etc."
              value={firmwareVersion}
              disabled={props.type === "Edit"}
              onChange={(e) => {
                setFirmwareVersion(e.target.value);
                setErrors({
                  versionNumber: null,
                });
              }}
            />
          </Form.Field>
          <Form.Field>
            <label htmlFor="upload_file">
              Upload File from the system:{" "}
              {props.type === "Edit" ? "(not allowed while editing)" : ""}
            </label>
            <div
              style={{
                position: "relative",
                marginTop: "12px",
                cursor: props.type === "Edit" ? "not-allowed" : "pointer",
              }}
            >
              <StyledFileUploadButton
                fluid
                content="Select File"
                labelPosition="left"
                icon="file"
                disabled={props.type === "Edit"}
              />
              <StyledFileInput
                id="upload_file"
                type="file"
                ref={firmwareFileInput}
                disabled={props.type === "Edit"}
                onChange={onSelect}
              />
            </div>
            <label htmlFor="file_name" style={{ marginTop: "12px" }}>
              File Chosen:{" "}
            </label>
            <span id="file_name">
              {firmwareFileName !== "" ? firmwareFileName : "No File Chosen"}
            </span>
          </Form.Field>
          {props?.componentsEnabled === false && (
            <Form.Field>
              <label
                htmlFor="firmware_uncompressed_size"
                style={{ display: "flex", gap: "8px" }}
              >
                Uncompressed File Size (Optional)
                <Popup
                  inverted
                  trigger={
                    <Icon
                      name="question circle"
                      style={{ marginBottom: "2px", marginLeft: "2px" }}
                    />
                  }
                  content={
                    "Estimated disk space needed to successfully uncompress file on your device"
                  }
                  position="top center"
                />
              </label>
              <div
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  gap: "12px",
                  width: "100%",
                }}
              >
                <Input
                  min="0"
                  type="number"
                  id="firmware_uncompressed_size"
                  placeholder="value"
                  value={uncompressedSize}
                  onChange={(e) => {
                    const value =
                      unit === "B"
                        ? parseInt(e.target.value)
                        : parseFloat(e.target.value);
                    setUncompressedSize(value);
                  }}
                  step={unit === "B" ? "1" : "0.01"}
                  style={{ minWidth: "calc(100% - 124px)" }}
                />
                <Dropdown
                  id="firmware_uncompressed_size_unit"
                  selection
                  options={[
                    { key: "B", value: "B", text: "B" },
                    { key: "KiB", value: "KiB", text: "KiB" },
                    { key: "MiB", value: "MiB", text: "MiB" },
                    { key: "GiB", value: "GiB", text: "GiB" },
                  ]}
                  value={unit}
                  onChange={(e, { value }) => {
                    setUnit(value as string);
                  }}
                  style={{ minWidth: "100px" }}
                />
              </div>
            </Form.Field>
          )}
          {showFirmwareUploadProgress && (
            <Form.Field>
              <label htmlFor="file-progress">
                {uploadedFirmwareFileResponse.status === 0 ? (
                  <span>
                    File Uploading
                    <AnimatedEllipsis spacing={3} dotSize={"8px"} />
                  </span>
                ) : (
                  <span>File Uploaded</span>
                )}
              </label>
              <progress
                id="file-progress"
                max={firmwareFileTotal}
                value={firmwareFileLoaded}
              />
            </Form.Field>
          )}
          {showFirmwareEditProgress && (
            <Form.Field>
              <label>
                Editing Firmware File{" "}
                <AnimatedEllipsis spacing={3} dotSize={"8px"} />
              </label>
            </Form.Field>
          )}
        </Form>

        {props.componentsEnabled && props.type === "Add" && (
          <Accordion fluid inverted>
            <Accordion.Title
              active={addDependencies}
              onClick={() => setAddDependencies(!addDependencies)}
              style={{ marginTop: "16px", fontSize: "16px", fontWeight: "700" }}
            >
              <Icon
                name={addDependencies ? "angle down" : "angle right"}
                style={{ marginRight: "4px", fontSize: "16px" }}
              />
              Add Dependencies
            </Accordion.Title>
            <Accordion.Content active={addDependencies}>
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "24px",
                }}
              >
                {`Select the required component and the minimum firmware version that is
                required for the firmware version "${firmwareVersion}" of "${selectedComponent}" component to work properly.`}
              </p>

              <Table fixed>
                <Table.Body>
                  {dependencies.map((dependency) => (
                    <ComponentWiseFirmwareRow
                      key={dependency.component_name}
                      dependentComponentWithVersion={{
                        id: -1,
                        component_name: selectedComponent,
                        min_version: firmwareVersion,
                      }}
                      dependency={dependency}
                      dependencies={dependencies}
                      setDependencies={setDependencies}
                      componentsList={props.componentsList ?? []}
                      firmwareVersions={props.firmwareVersions ?? []}
                      loadingComponentList={props.loadingComponentList ?? false}
                    />
                  ))}
                </Table.Body>
              </Table>
            </Accordion.Content>
          </Accordion>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button
          id="cancel_button"
          secondary
          onClick={() => {
            onCloseModal();
          }}
        >
          <Icon name="remove" /> Discard
        </Button>
        <Button
          id="submit_button"
          disabled={disableUploadButton}
          primary
          onClick={async (e) => {
            await handleSubmit(e);
          }}
        >
          <Icon name="checkmark" />{" "}
          {props.type === "Edit" ? "Update" : "Upload"}
        </Button>
      </Modal.Actions>
    </Modal>
  );
});

export default CreateOrEditFirmwareModal;
